import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

/**
 * OpenInCAD — drop-in row of three buttons that try to launch the listing
 * directly inside Revit, SketchUp, or ArchiCAD via custom URL schemes,
 * falling back to the relevant download landing page if the plugin isn't
 * installed.
 *
 * URL scheme contract (the plugins register these on install — Revit /
 * SketchUp register their handler in v1.1; ArchiCAD plugin not built yet):
 *
 *   patinamart-revit://listing/<id>
 *   patinamart-sketchup://listing/<id>
 *   patinamart-archicad://listing/<id>
 *
 * Detection: we set a short `pageshow` / `visibilitychange` listener and
 * navigate to the scheme. If the OS handed off to a registered handler the
 * tab loses focus → we cancel the fallback. If 1.2 s passes with no
 * focus loss, the scheme didn't open anything → we send the user to the
 * plugin's download page (or a "request beta" mailto for ArchiCAD).
 */

const TOOLS = [
  {
    id: 'revit',
    label: 'Revit',
    scheme: 'patinamart-revit',
    fallback: '/revit',
    available: true,
  },
  {
    id: 'sketchup',
    label: 'SketchUp',
    scheme: 'patinamart-sketchup',
    fallback: '/sketchup',
    available: true,
  },
  {
    id: 'archicad',
    label: 'ArchiCAD',
    scheme: 'patinamart-archicad',
    fallback: 'mailto:hello@patinamart.com?subject=ArchiCAD%20plugin%20beta&body=I%27d%20like%20early%20access%20to%20the%20PatinaMart%20ArchiCAD%20plugin.',
    // ArchiCAD plugin isn't built yet — we still show the button (signals
    // intent + collects beta interest) but route it to a mailto.
    available: false,
  },
];

export default function OpenInCAD({ listingId, compact = false }) {
  const [busyId, setBusyId] = useState(null);

  if (!listingId) return null;

  const tryLaunch = (tool) => {
    if (busyId) return;
    setBusyId(tool.id);

    if (!tool.available) {
      // ArchiCAD path — straight to the beta-interest mailto.
      window.location.href = tool.fallback;
      setTimeout(() => setBusyId(null), 800);
      return;
    }

    let cancelled = false;
    const onHide = () => { cancelled = true; };
    document.addEventListener('visibilitychange', onHide, { once: true });
    window.addEventListener('blur', onHide, { once: true });

    // Fire the deep link via a hidden iframe so a missing handler doesn't
    // navigate the page away (browsers vary — some show an inline error
    // when assigning to location.href, others silently drop it).
    try {
      const ifr = document.createElement('iframe');
      ifr.style.display = 'none';
      ifr.src = `${tool.scheme}://listing/${encodeURIComponent(listingId)}`;
      document.body.appendChild(ifr);
      setTimeout(() => ifr.remove(), 1500);
    } catch {
      cancelled = false;
    }

    // After 1.2 s: if the OS didn't hand off to a registered plugin, the
    // tab is still focused → assume "no plugin installed" and route to the
    // download landing page.
    setTimeout(() => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('blur', onHide);
      setBusyId(null);
      if (!cancelled) {
        toast.message(`No ${tool.label} plugin detected — opening the install page.`);
        window.location.href = tool.fallback;
      }
    }, 1200);
  };

  if (compact) {
    return (
      <div className="flex items-center gap-1.5" data-testid={`open-in-cad-compact-${listingId}`}>
        {TOOLS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); tryLaunch(t); }}
            disabled={busyId === t.id}
            className="text-[11px] px-2 py-1 rounded-md border border-border/50 text-muted-foreground hover:text-accent hover:border-accent/40 transition-colors"
            data-testid={`open-in-${t.id}-${listingId}`}
          >
            {t.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-border/40 bg-card/40 p-5"
      data-testid={`open-in-cad-${listingId}`}
    >
      <h3 className="font-playfair text-lg font-semibold mb-1 flex items-center gap-2">
        <Box className="w-5 h-5 text-accent" />
        Open in your CAD tool
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        Jump straight to this listing inside Revit, SketchUp or ArchiCAD if you have the plugin installed.
        No plugin yet? We'll send you to the install page.
      </p>
      <div className="flex flex-wrap gap-2">
        {TOOLS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => tryLaunch(t)}
            disabled={busyId === t.id}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium border border-border/50 hover:border-accent/50 hover:text-accent transition-colors"
            data-testid={`open-in-${t.id}-detail`}
          >
            Open in {t.label}
            {!t.available && <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded-sm px-1.5 py-px">beta</span>}
            <ExternalLink className="w-3.5 h-3.5 opacity-60" />
          </button>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">
        Don't have the plugins?{' '}
        <Link to="/revit" className="text-accent hover:underline">Revit</Link>
        {' · '}
        <Link to="/sketchup" className="text-accent hover:underline">SketchUp</Link>
        {' · '}
        <a href={TOOLS[2].fallback} className="text-accent hover:underline">ArchiCAD beta interest</a>
      </p>
    </div>
  );
}
